import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ALL_ITEMS_PAGE_SIZE, PaginatedParams } from 'core/http/pagination';
import useProducts from './useProducts';
import useFormUtils from './useFormUtils';

const useProductNavigation = (onActiveProductParamsChange?: (params: Partial<PaginatedParams> | undefined) => void) => {
    const { declarationId, productId } = useParams();
    const { countryLowerCase: country, formType, internalType } = useFormUtils();
    const { products, listDeclarationProducts } = useProducts();

    const [activeProductParams, setActiveProductParams] = useState<Partial<PaginatedParams> | undefined>(undefined);
    const prevProductIdRef = useRef<string | undefined>(productId);
    const activeProductParamsRef = useRef(activeProductParams);
    activeProductParamsRef.current = activeProductParams;

    const updateActiveProductParams = useCallback(
        (params: Partial<PaginatedParams> | undefined) => {
            activeProductParamsRef.current = params;
            setActiveProductParams(params);
            onActiveProductParamsChange?.(params);
        },
        [onActiveProductParamsChange]
    );

    // When a productId is active but not present in the current page, the user arrived via a direct
    // URL (deep link, refresh, or prev/next crossing a page boundary before the list was updated).
    // Re-fetch the whole list so all products are in it and prev/next indices stay correct.
    useEffect(() => {
        if (!productId || !declarationId || !products?.total) return;
        if (products?.list.some((p: any) => p.id === productId)) return;

        const params = { size: ALL_ITEMS_PAGE_SIZE };
        updateActiveProductParams(params);
        listDeclarationProducts({ declarationId, formType, internalType, country, params });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, products?.total, products?.list.length]);

    // When entering a product view, clear any active query filter so prev/next navigation and item
    // counts use the full unfiltered list. When leaving, reset to default pagination.
    useEffect(() => {
        const prevProductId = prevProductIdRef.current;
        prevProductIdRef.current = productId;

        if (productId && !prevProductId && declarationId) {
            const currentParams = activeProductParamsRef.current;
            if (currentParams?.query !== undefined) {
                const { query: _q, ...paramsWithoutQuery } = currentParams;
                updateActiveProductParams(paramsWithoutQuery);
                listDeclarationProducts({ declarationId, formType, internalType, country, params: paramsWithoutQuery });
            }
        } else if (!productId && prevProductId && declarationId) {
            updateActiveProductParams(undefined);
            listDeclarationProducts({ declarationId, formType, internalType, country });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    const refetchProducts = useCallback(async () => {
        if (!declarationId) return;
        await listDeclarationProducts({ declarationId, formType, internalType, country, params: activeProductParams });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, declarationId, formType, internalType, activeProductParams]);

    const fetchProductPage = useCallback(
        async (params: Partial<PaginatedParams>) => {
            if (!declarationId) return undefined;
            updateActiveProductParams(params);
            return await listDeclarationProducts({ declarationId, formType, internalType, country, params });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [country, declarationId, formType, internalType, listDeclarationProducts, updateActiveProductParams]
    );

    return {
        activeProductParams,
        updateActiveProductParams,
        refetchProducts,
        fetchProductPage,
    };
};

export default useProductNavigation;
